<template>
  <div class="flex flex-col gap-3 text-left" data-testid="coverage-preview-summary">
    <div class="flex flex-wrap gap-2">
      <span class="chip chip-open" data-testid="coverage-preview-count-open"
        >Aberta <b>{{ plan.counts.OPEN }}</b></span
      >
      <span class="chip chip-doctor" data-testid="coverage-preview-count-doctor"
        >Com médico <b>{{ plan.counts.DOCTOR }}</b></span
      >
      <span class="chip chip-unidentified" data-testid="coverage-preview-count-unidentified"
        >Ocupado s/ ident. <b>{{ plan.counts.UNIDENTIFIED }}</b></span
      >
      <span
        v-if="plan.counts.BLOCKED > 0"
        class="chip chip-blocked"
        data-testid="coverage-preview-count-blocked"
        ><svg
          class="chip-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          data-testid="coverage-preview-blocked-icon"
        >
          <path :d="mdiLock" /></svg
        >Bloqueado <b>{{ plan.counts.BLOCKED }}</b></span
      >
    </div>
    <div class="rounded-lg border border-solid border-lightGray30 bg-gray-50 px-3 py-2">
      <p class="text-sm font-bold text-textPrimary" data-testid="coverage-preview-title">
        {{ title }}
      </p>
      <ul class="mt-1 flex flex-col gap-0.5 text-sm text-textPrimary">
        <li v-for="line in lines" :key="line" data-testid="coverage-preview-line">{{ line }}</li>
      </ul>
    </div>
    <!-- M6-C inline: com Antecipe no caminho, a orientação aparece já na prévia. -->
    <div
      v-if="hasAntecipeBlock"
      class="guidance rounded-lg px-3 py-2 text-sm"
      data-testid="coverage-preview-antecipe-guidance"
    >
      <p class="flex items-center gap-1.5 font-bold">
        <svg class="chip-icon" viewBox="0 0 24 24" aria-hidden="true"><path :d="mdiLock" /></svg>
        O que fazer:
      </p>
      <ol class="mt-1 flex list-decimal flex-col gap-0.5 pl-5">
        <li v-for="step in ANTECIPE_GUIDANCE_STEPS" :key="step">{{ step }}</li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiLock } from '@mdi/js'
import { describeCoveragePlan } from '../modals/utils/coverageAllocation'
import type { CoveragePlan } from '../modals/utils/coverageAllocation'

const props = withDefaults(
  defineProps<{ plan: CoveragePlan; targetDoctorName?: string | null }>(),
  {
    targetDoctorName: null
  }
)

const title = computed(() => {
  const n = props.plan.applicableRows.length
  if (n === 0) return 'Nenhum plantão será alterado'
  return `O que vai acontecer em ${n} ${n === 1 ? 'plantão' : 'plantões'}`
})
// Mesmos 3 passos do estado de bloqueio do M6-C (CoverageTransitionConfirmModal).
const ANTECIPE_GUIDANCE_STEPS = [
  'Confirme com o financeiro se o Antecipe foi de fato pago.',
  'Se o médico realizou o plantão, mantenha-o alocado.',
  'Se não realizou, trate o estorno antes de mudar a cobertura.'
]
const hasAntecipeBlock = computed(() =>
  props.plan.rows.some((row) => row.action === 'IGNORED' && row.blockedReason === 'ANTECIPE')
)
const lines = computed(() =>
  describeCoveragePlan(props.plan, { targetDoctorName: props.targetDoctorName })
)
</script>

<style scoped>
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  border: 1px solid;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
}
.chip-open {
  background: var(--st-open-bg, #fef3e7);
  border-color: var(--st-open-stripe, #f18f1b);
  color: var(--st-open-text, #8a4b00);
}
.chip-doctor {
  background: #e0f2f6;
  border-color: #84c8d7;
  color: #235e6c;
}
.chip-unidentified {
  background: repeating-linear-gradient(135deg, #eeeef3 0 5px, #e1e1ea 5px 10px);
  border-color: #bcbccd;
  color: #3e3e5a;
}
.chip-icon {
  width: 14px;
  height: 14px;
  fill: currentColor;
}
.guidance {
  background: #fdecec;
  border: 1px solid #f3a5a9;
  color: #9b1b21;
}
/* M4: bloqueado em estilo de alerta (vermelho), como no mockup. */
.chip-blocked {
  background: #fdecec;
  border-color: #f3a5a9;
  color: #da1e28;
}
</style>
